import {
  type RemoteConfig,
  fetchAndActivate,
  activate,
  onConfigUpdate,
} from 'firebase/remote-config';
import { logger } from '@/shared/common/utils/logger.util';
import {
  BASE_DELAY_MS,
  MAX_RETRIES,
} from '@/shared/firebase/constants/remoteConfig.constant';

/**
 * Fetch inicial y activación de Remote Config
 * Solo se ejecuta una vez al iniciar la app
 * @returns true si hay valores nuevos, false si usa caché
 */
export const fetchAndActivateConfig = async (
  remoteConfig: RemoteConfig
): Promise<boolean> => {
  try {
    return await fetchAndActivate(remoteConfig);
  } catch (error) {
    logger.error('[Remote Config] Error en fetch inicial:', {
      error,
    });
    throw error;
  }
};

const getRetryDelay = (attempt: number): number => {
  const exponentialDelay = BASE_DELAY_MS * Math.pow(2, attempt - 1);
  const jitter = Math.random() * BASE_DELAY_MS;
  return exponentialDelay + jitter;
};

/**
 * Configura listener de actualizaciones en tiempo real (WebSocket)
 * con reconexión automática usando exponential backoff.
 *
 * @param remoteConfig - Instancia de Remote Config
 * @param onUpdate - Callback cuando hay actualizaciones activadas
 * @returns Función para detener el listener y liberar recursos
 */
export const setupRealtimeListener = (
  remoteConfig: RemoteConfig,
  onUpdate: () => void
): (() => void) => {
  let retryCount = 0;
  let retryTimeout: ReturnType<typeof setTimeout> | null = null;
  let currentUnsubscribe: (() => void) | null = null;
  let isStopped = false;
  let isConnecting = false;

  const clearPendingRetry = (): void => {
    if (retryTimeout) {
      clearTimeout(retryTimeout);
      retryTimeout = null;
    }
  };

  const scheduleRetry = (): void => {
    if (isStopped) return;

    if (retryCount >= MAX_RETRIES) {
      logger.error(
        `[Remote Config] Listener detenido tras ${MAX_RETRIES} intentos fallidos`
      );
      return;
    }

    retryCount++;
    const delay = getRetryDelay(retryCount);

    logger.warn(
      `[Remote Config] Reconectando (${retryCount}/${MAX_RETRIES}) en ${Math.round(delay)}ms...`
    );

    clearPendingRetry();
    retryTimeout = setTimeout(() => {
      retryTimeout = null;
      if (!isStopped) connect();
    }, delay);
  };

  const connect = (): void => {
    if (isStopped || isConnecting) return;
    isConnecting = true;

    currentUnsubscribe = onConfigUpdate(remoteConfig, {
      next: async (configUpdate) => {
        isConnecting = false;
        retryCount = 0;

        const updatedKeys = configUpdate.getUpdatedKeys();
        if (updatedKeys.size === 0) return;

        try {
          await activate(remoteConfig);
          onUpdate();
        } catch (error) {
          logger.error('[Remote Config] Error al activar actualización', {
            error,
          });
        }
      },
      error: (error) => {
        isConnecting = false;
        logger.warn('[Remote Config] WebSocket error:', {
          error: error.message,
        });
        scheduleRetry();
      },
      complete: () => {
        isConnecting = false;
        if (!isStopped) {
          logger.warn('[Remote Config] Listener desconectado inesperadamente');
          scheduleRetry();
        }
      },
    });
  };

  connect();

  return () => {
    isStopped = true;
    clearPendingRetry();
    if (currentUnsubscribe) {
      currentUnsubscribe();
      currentUnsubscribe = null;
    }
  };
};
